import { z } from "zod";

export const TASK_PRESENCE_TTL_MS = 15_000;
export const CHAT_TYPING_TTL_MS = 3_000;

export enum PresenceKind {
    TASK = "task",
    WORK = "work",
    CHAT = "chat",
    /** Observers announce themselves so live viewers answer with a heartbeat; the target id is the kind being observed. */
    WATCH = "watch",
}

export enum PresenceStatus {
    JOINED = "joined",
    ACTIVE = "active",
    LEFT = "left",
}

export const presenceMessageSchema = z.object({
    status: z.nativeEnum(PresenceStatus),
});
export type PresenceMessage = z.infer<typeof presenceMessageSchema>;

/**
 * Presence lives in one of two namespaces. A workspace namespace is scoped to a
 * single workspace; an organization namespace spans every client workspace of
 * the organization, so one connection can observe them all.
 */
export type PresenceScope =
    | { workspaceId: string; organizationId?: undefined }
    | { organizationId: string; workspaceId?: undefined };

export interface PresenceTargetParams {
    workspaceId: string;
    kind: PresenceKind;
    targetId: string;
    /** Set to address the organization namespace; omit for the workspace namespace */
    organizationId?: string;
}

export interface PresenceChannelParams {
    scope: PresenceScope;
    kind: PresenceKind;
    /** Narrows the channel to one target of the workspace namespace */
    targetId?: string;
}

export interface PresenceHeartbeatParams extends PresenceTargetParams {
    userId: number;
}

interface WorkspaceUserParams {
    workspaceId: string;
    userId: number;
}

interface OrganizationUserParams {
    organizationId: string;
    userId: number;
}

interface OrganizationWorkspaceParams {
    organizationId: string;
    workspaceId: string;
}

const WORKSPACE_SUBJECT_TOKEN_COUNT = 6;
const ORGANIZATION_SUBJECT_TOKEN_COUNT = 7;
const USER_ID_PATTERN = /^\d+$/;

/** Workspace token of probes, which watch a whole organization rather than one workspace */
export const PRESENCE_ALL_WORKSPACES = "all";

export const getPresenceTtlMs = (kind: PresenceKind): number =>
    kind === PresenceKind.CHAT ? CHAT_TYPING_TTL_MS : TASK_PRESENCE_TTL_MS;

const getPresenceTargetPrefix = ({
    organizationId,
    workspaceId,
    kind,
    targetId,
}: PresenceTargetParams): string =>
    organizationId
        ? `organizations.${organizationId}.presence.${kind}.${workspaceId}.${targetId}`
        : `workspaces.${workspaceId}.presence.${kind}.${targetId}`;

/**
 * Presence is client to client: each client publishes on a subject that ends
 * with its own user id and subscribes to everyone else's. Tokens only allow
 * publishing on the holder's own user id (see `getPresencePublishPermission`),
 * so a subscriber can trust the id parsed from the subject.
 */
export const getPresenceHeartbeatSubject = ({
    userId,
    ...target
}: PresenceHeartbeatParams): string =>
    `${getPresenceTargetPrefix(target)}.${userId}`;

export const getPresenceSubscriptionSubject = (
    target: PresenceTargetParams,
): string => `${getPresenceTargetPrefix(target)}.*`;

export const getPresenceChannelSubject = ({
    scope,
    kind,
    targetId,
}: PresenceChannelParams): string => {
    if (scope.organizationId !== undefined) {
        return `organizations.${scope.organizationId}.presence.${kind}.*.*.*`;
    }

    return `workspaces.${scope.workspaceId}.presence.${kind}.${targetId ?? "*"}.*`;
};

interface PresenceProbeParams {
    scope: PresenceScope;
    observedKind: PresenceKind;
}

export const getPresenceProbeTarget = ({
    scope,
    observedKind,
}: PresenceProbeParams): PresenceTargetParams =>
    scope.organizationId !== undefined
        ? {
              organizationId: scope.organizationId,
              workspaceId: PRESENCE_ALL_WORKSPACES,
              kind: PresenceKind.WATCH,
              targetId: observedKind,
          }
        : {
              workspaceId: scope.workspaceId,
              kind: PresenceKind.WATCH,
              targetId: observedKind,
          };

export const getPresencePublishPermission = ({
    workspaceId,
    userId,
}: WorkspaceUserParams): string =>
    `workspaces.${workspaceId}.presence.*.*.${userId}`;

export const getPresenceSubscribePermission = (workspaceId: string): string =>
    `workspaces.${workspaceId}.presence.>`;

export const getOrganizationPresencePublishPermission = ({
    organizationId,
    userId,
}: OrganizationUserParams): string =>
    `organizations.${organizationId}.presence.*.*.*.${userId}`;

export const getOrganizationPresenceSubscribePermission = (
    organizationId: string,
): string => `organizations.${organizationId}.presence.>`;

export const getOrganizationWorkspacePresenceDenyPermission = ({
    organizationId,
    workspaceId,
}: OrganizationWorkspaceParams): string =>
    `organizations.${organizationId}.presence.*.${workspaceId}.>`;

const isPresenceKind = (value: string): value is PresenceKind =>
    Object.values<string>(PresenceKind).includes(value);

export const parsePresenceHeartbeatSubject = (
    subject: string,
): PresenceHeartbeatParams | null => {
    const tokens = subject.split(".");
    const [root, namespaceId, branch, kind] = tokens;

    const isOrganizationSubject =
        root === "organizations" &&
        tokens.length === ORGANIZATION_SUBJECT_TOKEN_COUNT;
    const isWorkspaceSubject =
        root === "workspaces" &&
        tokens.length === WORKSPACE_SUBJECT_TOKEN_COUNT;
    if (!isOrganizationSubject && !isWorkspaceSubject) {
        return null;
    }

    const [workspaceId, targetId, userIdToken] = isOrganizationSubject
        ? tokens.slice(4)
        : [namespaceId, ...tokens.slice(4)];
    const isHeartbeatSubject =
        branch === "presence" &&
        isPresenceKind(kind) &&
        !!namespaceId &&
        !!workspaceId &&
        !!targetId &&
        USER_ID_PATTERN.test(userIdToken);
    if (!isHeartbeatSubject) {
        return null;
    }

    const userId = Number(userIdToken);
    if (!Number.isSafeInteger(userId)) {
        return null;
    }

    return {
        ...(isOrganizationSubject ? { organizationId: namespaceId } : {}),
        workspaceId,
        kind,
        targetId,
        userId,
    };
};
